import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SubscriptionsOutlinedIcon from '@mui/icons-material/SubscriptionsOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useStripeApi } from '../../hooks/useStripeApi';
import {
  aggregatePaidInvoicesBySubscription,
  estimateMrrFromSubscription,
  formatCustomerDisplay,
  formatSubscriptionPlanDisplay,
  stripeDashboardBase,
  type StripePaidAgg,
  type StripeSubscription,
} from '../../services/stripeApi';
import { listStripePaidInvoicesBatchResolved, listStripeSubscriptionsResolved } from '../../services/stripeResolved';
import { fmtStripeMoney, fmtStripeUnix, StripeHero, StripeStatCard, StripeStatusChip, subStatusColor } from '../../components/stripe/stripeShared';

const StripeSubscriptionsPage: React.FC = () => {
  const { settings, isSecretConfigured, localSecretKey } = useStripeApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const dashBase = localSecretKey ? stripeDashboardBase(localSecretKey) : 'https://dashboard.stripe.com';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subs, setSubs] = useState<StripeSubscription[]>([]);
  const [subPaidMap, setSubPaidMap] = useState<Map<string, StripePaidAgg>>(new Map());
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpandedId(null);
    try {
      const [res, paidInv] = await Promise.all([
        listStripeSubscriptionsResolved(settings, isSecretConfigured, {
          limit: 50,
          ...(status ? { status } : {}),
        }),
        listStripePaidInvoicesBatchResolved(settings, isSecretConfigured, 100),
      ]);
      setSubs(res.data);
      setSubPaidMap(aggregatePaidInvoicesBySubscription(paidInv));
      setHasMore(res.has_more);
      const last = res.data[res.data.length - 1];
      setCursor(last?.id);
    } catch (e) {
      const msg = axios.isAxiosError(e) ? e.response?.data?.error?.message || e.message : 'Failed';
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  }, [settings, isSecretConfigured, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyFilters = () => {
    const p = new URLSearchParams();
    if (status) {
      p.set('status', status);
    }
    if (search) {
      p.set('search', search);
    }
    setSearchParams(p, { replace: true });
    void load();
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return subs;
    }
    return subs.filter((s) => {
      const plan = formatSubscriptionPlanDisplay(s).toLowerCase();
      const cust = formatCustomerDisplay(s.customer).toLowerCase();
      return plan.includes(q) || cust.includes(q) || s.status.includes(q);
    });
  }, [subs, search]);

  const summary = useMemo(() => {
    let mrrCents = 0;
    let currency = 'usd';
    let active = 0;
    let totalPaid = 0;
    for (const s of filtered) {
      if (s.status === 'active') {
        active += 1;
      }
      const mrr = estimateMrrFromSubscription(s);
      mrrCents += mrr.cents;
      currency = mrr.currency;
      const agg = subPaidMap.get(s.id);
      if (agg) {
        totalPaid += agg.totalCents;
      }
    }
    return { count: filtered.length, mrrCents, currency, active, totalPaid };
  }, [filtered, subPaidMap]);

  const loadMore = async () => {
    if (!cursor) {
      return;
    }
    setLoading(true);
    try {
      const res = await listStripeSubscriptionsResolved(settings, isSecretConfigured, {
        limit: 25,
        starting_after: cursor,
        ...(status ? { status } : {}),
      });
      setSubs((prev) => [...prev, ...res.data]);
      setHasMore(res.has_more);
      const last = res.data[res.data.length - 1];
      setCursor(last?.id);
    } catch (e) {
      const msg = axios.isAxiosError(e) ? e.response?.data?.error?.message || e.message : 'Failed';
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <StripeHero
          compact
          title="Stripe · Subscriptions"
          subtitle="Filter by status and search plan or customer. MRR estimated from price intervals."
        />
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StripeStatCard
            icon={<SubscriptionsOutlinedIcon />}
            label="Subscriptions"
            value={String(summary.count)}
            sub="Matching filters"
            color="#635bff"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StripeStatCard
            icon={<TrendingUpOutlinedIcon />}
            label="Est. MRR"
            value={fmtStripeMoney(summary.mrrCents, summary.currency)}
            sub="On filtered list"
            color="#0d9488"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StripeStatCard
            icon={<SubscriptionsOutlinedIcon />}
            label="Active"
            value={String(summary.active)}
            sub="status=active"
            color="#db2777"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StripeStatCard
            icon={<PaidOutlinedIcon />}
            label="Total paid (sample)"
            value={fmtStripeMoney(summary.totalPaid, summary.currency)}
            sub="Paid invoices batch"
            color="#ea580c"
          />
        </Grid>
      </Grid>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value="">All statuses</MenuItem>
                <MenuItem value="active">active</MenuItem>
                <MenuItem value="trialing">trialing</MenuItem>
                <MenuItem value="past_due">past_due</MenuItem>
                <MenuItem value="canceled">canceled</MenuItem>
                <MenuItem value="unpaid">unpaid</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Search"
              size="small"
              placeholder="Plan or customer"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 220 }}
            />
            <Button variant="contained" onClick={applyFilters}>
              Apply
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Card>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={44} />
              <TableCell>Plan</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Est. MRR</TableCell>
              <TableCell>Total paid</TableCell>
              <TableCell>Renews</TableCell>
              <TableCell align="right">Open</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((s) => {
              const mrr = estimateMrrFromSubscription(s);
              const plan = formatSubscriptionPlanDisplay(s);
              const agg = subPaidMap.get(s.id);
              const tx = agg?.transactions ?? [];
              const open = expandedId === s.id;
              return (
                <React.Fragment key={s.id}>
                  <TableRow hover selected={open}>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => setExpandedId(open ? null : s.id)}
                        disabled={tx.length === 0}
                      >
                        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <Typography fontWeight={800}>{plan}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography fontWeight={600}>{formatCustomerDisplay(s.customer)}</Typography>
                    </TableCell>
                    <TableCell>
                      <StripeStatusChip label={s.status} color={subStatusColor(s.status)} />
                    </TableCell>
                    <TableCell>{fmtStripeMoney(mrr.cents, mrr.currency)}</TableCell>
                    <TableCell>
                      {agg ? fmtStripeMoney(agg.totalCents, agg.currency) : '—'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtStripeUnix(s.current_period_end)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Stripe Dashboard">
                        <Link href={`${dashBase}/subscriptions/${s.id}`} target="_blank" rel="noopener noreferrer">
                          <OpenInNewIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />
                        </Link>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 0, borderBottom: open ? undefined : 0 }} colSpan={8}>
                      <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 1, bgcolor: 'grey.50', borderRadius: 1, mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                            Paid invoice payments
                          </Typography>
                          {tx.length ? (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Date</TableCell>
                                  <TableCell>Invoice</TableCell>
                                  <TableCell align="right">Amount</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {tx.map((t) => (
                                  <TableRow key={t.id}>
                                    <TableCell>{fmtStripeUnix(t.created)}</TableCell>
                                    <TableCell>{t.number || 'Invoice'}</TableCell>
                                    <TableCell align="right">{fmtStripeMoney(t.amountPaid, t.currency)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No paid invoices in sample.
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography color="text.secondary" py={4} textAlign="center">
                    No subscriptions for this filter.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {hasMore && (
        <Button sx={{ mt: 2 }} disabled={loading} onClick={() => void loadMore()}>
          {loading ? <CircularProgress size={22} /> : 'Load more'}
        </Button>
      )}
    </Box>
  );
};

export default StripeSubscriptionsPage;
